import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { apiCall } from '../AuthContext';
import { Printer, Plus, Save, Trash2, Loader2, CheckCircle2, AlertCircle, Copy, Star, StarOff, Tag, LayoutGrid, ChevronRight, RefreshCw } from 'lucide-react';

const SIZES = [
  { id: '2x1', label: '2" × 1"', desc: 'Small tag' },
  { id: '3x2', label: '3" × 2"', desc: 'Standard' },
  { id: '4x6', label: '4" × 6"', desc: 'Shipping' },
];

const ELEMENT_LABELS = {
  store_name: 'Store Name',
  product_name: 'Product Name',
  retail_price: 'Retail Price',
  our_price: 'Our Price',
  discount_banner: 'Discount Banner',
  barcode: 'Barcode',
  footer: 'Footer Text',
};

const DEFAULT_SAMPLE = { name: 'Sample Product Name', retail: '19.99', our: '9.99', barcode: '0012345678901' };

export default function LabelDesigner() {
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [size, setSize] = useState('2x1');
  const [name, setName] = useState('My Label');
  const [elements, setElements] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState(null);
  const [sample, setSample] = useState(DEFAULT_SAMPLE);
  const [isDirty, setIsDirty] = useState(false);
  const debounceRef = useRef(null);

  const canvas = useMemo(() => ({
    '2x1': { w: 609, h: 305 },
    '3x2': { w: 914, h: 610 },
    '4x6': { w: 1218, h: 1829 },
  }[size]), [size]);

  const refreshList = useCallback(async () => {
    try {
      const r = await apiCall('/api/label-templates', { method: 'GET' });
      setTemplates(r.data || []);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { refreshList(); }, [refreshList]);

  const loadDefaultsFor = useCallback(async (s) => {
    const r = await apiCall(`/api/label-templates/defaults/${s}`, { method: 'GET' });
    setElements(r.data.elements || []);
    setIsDirty(true);
  }, []);

  const newTemplate = async (s = '2x1') => {
    setActiveId(null);
    setSize(s);
    setName('My Label');
    await loadDefaultsFor(s);
    setIsDirty(true);
  };

  const loadTemplate = (t) => {
    setActiveId(t.id);
    setSize(t.size || '2x1');
    setName(t.name || 'My Label');
    setElements(Array.isArray(t.elements) && t.elements.length ? t.elements : []);
    // If old-style template without elements, fetch defaults for the size
    if (!Array.isArray(t.elements) || t.elements.length === 0) {
      loadDefaultsFor(t.size || '2x1');
    }
    setIsDirty(false);
  };

  const runPreview = useCallback(async () => {
    if (!elements || elements.length === 0) return;
    setPreviewing(true);
    try {
      const r = await apiCall('/api/label-templates/preview', {
        method: 'POST',
        data: { size, elements, sample_product_name: sample.name, sample_retail: sample.retail, sample_our_price: sample.our, sample_barcode: sample.barcode },
      });
      setPreview(r.data.image);
    } catch (e) {
      setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Preview failed' });
    } finally { setPreviewing(false); }
  }, [elements, size, sample]);

  // Debounced live preview
  useEffect(() => {
    if (!elements || elements.length === 0) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runPreview, 450);
    return () => clearTimeout(debounceRef.current);
  }, [elements, size, sample, runPreview]);

  const updateEl = (idx, patch) => {
    setElements(prev => prev.map((e, i) => i === idx ? { ...e, ...patch } : e));
    setIsDirty(true);
  };
  const moveEl = (idx, dir) => {
    setElements(prev => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      const [el] = arr.splice(idx, 1);
      arr.splice(j, 0, el);
      return arr;
    });
    setIsDirty(true);
  };

  const saveTemplate = async () => {
    setSaving(true); setStatus(null);
    try {
      if (activeId) {
        await apiCall(`/api/label-templates/${activeId}`, { method: 'PUT', data: { name, size, elements } });
        setStatus({ kind: 'success', msg: 'Saved' });
      } else {
        const r = await apiCall('/api/label-templates', { method: 'POST', data: { name, size, elements } });
        setActiveId(r.data.id);
        setStatus({ kind: 'success', msg: 'Template created' });
      }
      setIsDirty(false);
      await refreshList();
    } catch (e) {
      setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Save failed' });
    } finally { setSaving(false); }
  };

  const duplicateTemplate = async () => {
    setSaving(true); setStatus(null);
    try {
      const r = await apiCall('/api/label-templates', { method: 'POST', data: { name: `${name} (Copy)`, size, elements } });
      setActiveId(r.data.id);
      setName(r.data.name);
      setStatus({ kind: 'success', msg: 'Duplicated' });
      await refreshList();
    } catch (e) {
      setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Duplicate failed' });
    } finally { setSaving(false); }
  };

  const deleteTemplate = async () => {
    if (!activeId) return;
    if (!window.confirm('Delete this template?')) return;
    setDeleting(true); setStatus(null);
    try {
      await apiCall(`/api/label-templates/${activeId}`, { method: 'DELETE' });
      setActiveId(null);
      setElements([]);
      setPreview(null);
      await refreshList();
      setStatus({ kind: 'success', msg: 'Deleted' });
    } catch (e) {
      setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Delete failed' });
    } finally { setDeleting(false); }
  };

  const toggleDefault = async (t) => {
    try {
      await apiCall(`/api/label-templates/${t.id}`, { method: 'PUT', data: { is_default: !t.is_default } });
      await refreshList();
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="label-designer-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#0F172A] tracking-tight">Label Designer</h1>
        <p className="text-slate-500 mt-1 text-base">Create, customize and preview your label templates live.</p>
      </div>

      {status && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border ${status.kind === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="ld-status">
          {status.kind === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {status.msg}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-5">
        {/* Left: Templates list */}
        <div className="lg:col-span-3 space-y-3" data-testid="ld-template-list">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Templates</span>
            <button onClick={() => newTemplate(size)} className="inline-flex items-center gap-1 text-xs text-[#0052FF] hover:underline font-semibold" data-testid="ld-new-btn"><Plus className="w-3.5 h-3.5" /> New</button>
          </div>
          <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1">
            {templates.length === 0 && (
              <div className="text-xs text-slate-400 p-3 border border-dashed border-slate-200 rounded-lg">No templates yet. Click <strong>New</strong> to create one.</div>
            )}
            {templates.map(t => (
              <button key={t.id} onClick={() => loadTemplate(t)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${activeId === t.id ? 'bg-[#0052FF] text-white border-[#0052FF]' : 'bg-white border-slate-200 hover:border-[#0052FF] hover:bg-slate-50'}`}
                data-testid={`ld-tpl-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold truncate">{t.name || 'Unnamed'}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${activeId === t.id ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>{t.size || '—'}</span>
                </div>
                <div className={`text-[11px] mt-0.5 flex items-center gap-2 ${activeId === t.id ? 'text-white/70' : 'text-slate-400'}`}>
                  {t.username === '_global' ? 'Global' : 'Mine'}
                  {t.is_default && <span className="inline-flex items-center gap-0.5"><Star className="w-2.5 h-2.5 fill-current" /> default</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Middle: Preview canvas */}
        <div className="lg:col-span-5 space-y-3 lg:sticky lg:top-4 lg:self-start" data-testid="ld-preview-col">
          <div className="flex items-center gap-2 flex-wrap">
            <LayoutGrid className="w-4 h-4 text-slate-500" />
            {SIZES.map(s => (
              <button key={s.id} onClick={() => { setSize(s.id); if (!activeId) loadDefaultsFor(s.id); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${size === s.id ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white border-slate-200 text-slate-700 hover:border-[#0F172A]'}`}
                data-testid={`ld-size-${s.id}`}>
                {s.label}<span className={`ml-1 text-[10px] ${size === s.id ? 'text-white/60' : 'text-slate-400'}`}>{s.desc}</span>
              </button>
            ))}
            <button onClick={runPreview} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#0052FF]" data-testid="ld-refresh-preview">
              <RefreshCw className={`w-3.5 h-3.5 ${previewing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-4 flex items-center justify-center min-h-[280px] max-h-[55vh] overflow-hidden" data-testid="ld-canvas">
            {previewing && !preview && <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />}
            {preview && (
              <img src={preview} alt="label preview" className="max-w-full max-h-[50vh] bg-white shadow-lg border border-slate-300 object-contain" style={{ aspectRatio: `${canvas.w} / ${canvas.h}` }} />
            )}
            {!previewing && !preview && elements.length === 0 && (
              <div className="text-center text-slate-400 text-sm">
                <Tag className="w-10 h-10 mx-auto mb-2 opacity-40" />
                Click "New" to start designing, or pick a template on the left.
              </div>
            )}
          </div>

          {/* Sample data */}
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="ld-sample">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">Preview with sample data</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <input value={sample.name} onChange={e => setSample(s => ({ ...s, name: e.target.value }))} placeholder="Product name"
                className="px-2 py-1.5 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]" data-testid="ld-sample-name" />
              <input value={sample.retail} onChange={e => setSample(s => ({ ...s, retail: e.target.value }))} placeholder="Retail $"
                className="px-2 py-1.5 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]" data-testid="ld-sample-retail" />
              <input value={sample.our} onChange={e => setSample(s => ({ ...s, our: e.target.value }))} placeholder="Our price $"
                className="px-2 py-1.5 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]" data-testid="ld-sample-our" />
              <input value={sample.barcode} onChange={e => setSample(s => ({ ...s, barcode: e.target.value }))} placeholder="Barcode"
                className="px-2 py-1.5 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF] font-mono" data-testid="ld-sample-barcode" />
            </div>
          </div>
        </div>

        {/* Right: Element controls */}
        <div className="lg:col-span-4 space-y-3" data-testid="ld-elements">
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Template name</label>
              <input value={name} onChange={e => { setName(e.target.value); setIsDirty(true); }}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0052FF]" data-testid="ld-name-input" />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={saveTemplate} disabled={saving || elements.length === 0}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-lg py-2 text-sm disabled:opacity-50" data-testid="ld-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {activeId ? (isDirty ? 'Save changes' : 'Saved') : 'Create template'}
              </button>
              {activeId && (
                <>
                  <button onClick={duplicateTemplate} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:text-[#0052FF] hover:border-[#0052FF]" title="Duplicate" data-testid="ld-duplicate-btn"><Copy className="w-4 h-4" /></button>
                  <button onClick={deleteTemplate} disabled={deleting} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:text-red-500 hover:border-red-300 disabled:opacity-50" title="Delete" data-testid="ld-delete-btn">{deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</button>
                </>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 px-1">Elements · {elements.length}</p>
            {elements.length === 0 && <p className="text-xs text-slate-400 px-1">Select or create a template to begin.</p>}
            {elements.map((el, idx) => (
              <ElementRow key={idx} el={el} size={size} onChange={(p) => updateEl(idx, p)} onMove={(d) => moveEl(idx, d)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ElementRow({ el, size, onChange, onMove }) {
  const [open, setOpen] = useState(true);
  const label = ELEMENT_LABELS[el.type] || el.type;
  const canvasH = { '2x1': 305, '3x2': 610, '4x6': 1829 }[size] || 305;
  const fontMax = { '2x1': 80, '3x2': 140, '4x6': 300 }[size] || 80;
  const bcMaxH = { '2x1': 200, '3x2': 400, '4x6': 800 }[size] || 200;
  const step = (cur, d) => Math.max(1, Number(cur || 0) + d);
  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${el.enabled ? 'border-slate-200' : 'border-slate-200 bg-slate-50'}`} data-testid={`ld-el-${el.type}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <input type="checkbox" checked={!!el.enabled} onChange={e => onChange({ enabled: e.target.checked })} data-testid={`ld-el-${el.type}-toggle`} />
        <button onClick={() => setOpen(o => !o)} className="flex-1 text-left">
          <span className={`text-sm font-semibold ${el.enabled ? 'text-[#0F172A]' : 'text-slate-400'}`}>{label}</span>
          {!open && el.enabled && (
            <span className="ml-2 text-[10px] text-slate-400">{el.type === 'barcode' ? `bar ${el.module_width || 3} · h${el.height || 120}` : `${el.font_size || 30}pt · ${el.align || 'center'}`}</span>
          )}
        </button>
        <button onClick={() => onMove(-1)} className="text-slate-400 hover:text-slate-700 text-xs">↑</button>
        <button onClick={() => onMove(1)} className="text-slate-400 hover:text-slate-700 text-xs">↓</button>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform cursor-pointer ${open ? 'rotate-90' : ''}`} onClick={() => setOpen(o => !o)} />
      </div>
      {open && el.enabled && (
        <div className="px-3 pb-3 pt-0 space-y-3 border-t border-slate-100">
          {/* Editable label text for text-based elements */}
          {el.type !== 'barcode' && el.type !== 'store_name' && el.type !== 'product_name' && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                {el.type === 'discount_banner' ? 'Banner text (use {disc_pct})' : 'Label text (change to whatever you want)'}
              </label>
              <input value={el.text || ''} onChange={e => onChange({ text: e.target.value })}
                placeholder={el.type === 'retail_price' ? 'e.g. Retail, MSRP, Was' : el.type === 'our_price' ? 'e.g. Our Price, Sale, Now' : ''}
                className="w-full px-2 py-1 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]" data-testid={`ld-el-${el.type}-text`} />
            </div>
          )}
          {el.type === 'store_name' && (
            <>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Store name (override)
                </label>
                <input value={el.text || ''} onChange={e => onChange({ text: e.target.value })}
                  placeholder="Leave empty to use your configured store name"
                  className="w-full px-2 py-1 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]"
                  data-testid="ld-el-store_name-text" />
                <p className="text-[10px] text-slate-400 mt-1">
                  Leave empty to use the store name from your account configuration.
                </p>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!el.uppercase} onChange={e => onChange({ uppercase: e.target.checked })} />
                Uppercase
              </label>
            </>
          )}
          {el.type === 'product_name' && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Max characters</label>
              <input type="number" min="10" max="120" value={el.max_chars || 40} onChange={e => onChange({ max_chars: Number(e.target.value) })} className="w-24 px-2 py-1 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]" />
            </div>
          )}
          {el.type !== 'barcode' ? (
            <>
              {/* Font size row with +/- */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Font size</label>
                  <span className="text-[11px] font-mono text-slate-500 tabular-nums">{el.font_size || 30}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onChange({ font_size: step(el.font_size, -2) })} className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold">−</button>
                  <input type="range" min="10" max={fontMax} value={el.font_size || 30} onChange={e => onChange({ font_size: Number(e.target.value) })} className="flex-1" data-testid={`ld-el-${el.type}-fs`} />
                  <button onClick={() => onChange({ font_size: step(el.font_size, 2) })} className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold">+</button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Align</label>
                <div className="grid grid-cols-3 gap-1">
                  {['left', 'center', 'right'].map(a => (
                    <button key={a} onClick={() => onChange({ align: a })}
                      className={`px-2 py-1 rounded text-xs font-semibold border ${el.align === a ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                      data-testid={`ld-el-${el.type}-align-${a}`}>{a}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vertical (Y)</label>
                  <span className="text-[11px] font-mono text-slate-500 tabular-nums">{el.y || 0} / {canvasH}</span>
                </div>
                <input type="range" min="0" max={canvasH} value={el.y || 0} onChange={e => onChange({ y: Number(e.target.value) })} className="w-full" data-testid={`ld-el-${el.type}-y`} />
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Bar thickness</label>
                  <span className="text-[11px] font-mono text-slate-500 tabular-nums">{el.module_width || 3} / 8</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onChange({ module_width: Math.max(1, (el.module_width || 3) - 1) })} className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold">−</button>
                  <input type="range" min="1" max="8" value={el.module_width || 3} onChange={e => onChange({ module_width: Number(e.target.value) })} className="flex-1" data-testid="ld-el-barcode-mw" />
                  <button onClick={() => onChange({ module_width: Math.min(8, (el.module_width || 3) + 1) })} className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold">+</button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Bar height</label>
                  <span className="text-[11px] font-mono text-slate-500 tabular-nums">{el.height || 120} / {bcMaxH}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onChange({ height: Math.max(20, (el.height || 120) - 10) })} className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold">−</button>
                  <input type="range" min="20" max={bcMaxH} step="5" value={el.height || 120} onChange={e => onChange({ height: Number(e.target.value) })} className="flex-1" data-testid="ld-el-barcode-h" />
                  <button onClick={() => onChange({ height: Math.min(bcMaxH, (el.height || 120) + 10) })} className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-xs font-bold">+</button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Align</label>
                <div className="grid grid-cols-3 gap-1">
                  {['left', 'center', 'right'].map(a => (
                    <button key={a} onClick={() => onChange({ align: a })}
                      className={`px-2 py-1 rounded text-xs font-semibold border ${el.align === a ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>{a}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vertical (Y)</label>
                  <span className="text-[11px] font-mono text-slate-500 tabular-nums">{el.y || 0} / {canvasH}</span>
                </div>
                <input type="range" min="0" max={canvasH} value={el.y || 0} onChange={e => onChange({ y: Number(e.target.value) })} className="w-full" />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!el.show_digits} onChange={e => onChange({ show_digits: e.target.checked })} />
                Show human-readable digits below bars
              </label>
            </>
          )}
        </div>
      )}
    </div>
  );
}
